/**
 * client/ui/profile.ts
 * Responsabilidad: Gestión del modal de perfil de usuario, estadísticas y progreso.
 */

import Swal from "sweetalert2";
import { profileManager, formatTime } from "../profile";
import { t } from "../i18n";
import { safeSetStorage } from "../utils";

export interface ProfileUIOptions {
    onOpen?: () => void;
    onClose?: () => void;
}

export class ProfileManagerUI {
    private currentAlias: string = "";
    private modalOpenedAt: number = 0;
    private options: ProfileUIOptions;

    // Elementos del DOM
    private profileBtn = document.getElementById("profile-btn");
    private profileModal = document.getElementById("profile-modal");
    private profileClose = document.getElementById("profile-close");
    private profileReset = document.getElementById("profile-reset");
    private profileChangeAlias = document.getElementById("profile-change-alias");
    private profileAliasValue = document.getElementById("profile-alias-value");
    private profileStatGames = document.getElementById("profile-stat-games");
    private profileStatTime = document.getElementById("profile-stat-time");
    private profileStatKills = document.getElementById("profile-stat-kills");
    private profileStatBestMass = document.getElementById("profile-stat-best-mass");
    private profileStatStreak = document.getElementById("profile-stat-streak");
    private profileStatBestStreak = document.getElementById("profile-stat-best-streak");
    private profileStatBestRank = document.getElementById("profile-stat-best-rank");
    private profileStatFavorite = document.getElementById("profile-stat-favorite");
    private profileStatCountry = document.getElementById("profile-stat-country");

    constructor(options: ProfileUIOptions = {}) {
        this.options = options;
        this.initEventListeners();
    }

    public setAlias(alias: string): void {
        this.currentAlias = alias;
    }

    public isOpen(): boolean {
        return Boolean(this.profileModal && !this.profileModal.classList.contains("hidden"));
    }

    public open(): void {
        this.refresh();
        if (this.profileModal) {
            this.profileModal.classList.remove("hidden");
            this.modalOpenedAt = performance.now();
        }
        if (this.options.onOpen) {
            this.options.onOpen();
        }
    }

    public close(): void {
        if (this.profileModal) {
            this.profileModal.classList.add("hidden");
        }
        if (this.options.onClose) {
            this.options.onClose();
        }
    }

    public toggle(): void {
        if (this.isOpen()) {
            this.close();
        } else {
            this.open();
        }
    }

    public async refresh(): Promise<void> {
        // 1. Mostrar inmediatamente datos de localStorage
        const p = profileManager.getProfile();
        this.renderStats(p, this.currentAlias);

        // 2. Consultar perfil actualizado en la base de datos de Supabase vía JWT
        const token = localStorage.getItem("alchem_auth_token") || localStorage.getItem("colyseus-auth-token");
        if (!token) return; // Si no hay token, usar los datos locales

        try {
            const res = await fetch("/api/profile", {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) return;

            const profile = await res.json();
            if (profile && Object.keys(profile).length > 0) {
                if (profile.alias) {
                    this.currentAlias = profile.alias;
                }
                // Actualizar los valores del DOM con los datos de la BD
                if (this.profileAliasValue) this.profileAliasValue.textContent = profile.alias || this.currentAlias || "—";
                if (this.profileStatGames) this.profileStatGames.textContent = String(profile.totalGames ?? p.gamesPlayed);
                if (this.profileStatTime) this.profileStatTime.textContent = formatTime(profile.totalTimeMs ?? p.totalTimeMs);
                if (this.profileStatKills) this.profileStatKills.textContent = String(profile.totalKills ?? p.totalKills);
                if (this.profileStatBestMass) this.profileStatBestMass.textContent = String(profile.bestMass ?? p.bestMass);
                if (this.profileStatStreak) this.profileStatStreak.textContent = String(profile.currentStreak ?? p.currentStreak);
                if (this.profileStatBestStreak) this.profileStatBestStreak.textContent = String(profile.bestStreak ?? p.bestStreak);
                if (this.profileStatBestRank) {
                    const rank = profile.bestRank ?? p.bestRank;
                    this.profileStatBestRank.textContent = rank > 0 ? `#${rank}` : "—";
                }
                if (this.profileStatCountry) {
                    this.profileStatCountry.textContent = profile.country || p.country || "—";
                }

                // Sincronizar el store local de profileManager
                profileManager.hydrate(profile);
            }
        } catch (e) {
            console.warn("[ProfileUI] Error al cargar perfil desde /api/profile:", e);
        }
    }

    private renderStats(p: ReturnType<typeof profileManager.getProfile>, alias: string): void {
        if (this.profileAliasValue) this.profileAliasValue.textContent = alias || "—";
        if (this.profileStatGames) this.profileStatGames.textContent = String(p.gamesPlayed);
        if (this.profileStatTime) this.profileStatTime.textContent = formatTime(p.totalTimeMs);
        if (this.profileStatKills) this.profileStatKills.textContent = String(p.totalKills);
        if (this.profileStatBestMass) this.profileStatBestMass.textContent = String(p.bestMass);
        if (this.profileStatStreak) this.profileStatStreak.textContent = String(p.currentStreak);
        if (this.profileStatBestStreak) this.profileStatBestStreak.textContent = String(p.bestStreak);
        if (this.profileStatBestRank) this.profileStatBestRank.textContent = p.bestRank > 0 ? `#${p.bestRank}` : "—";
        if (this.profileStatFavorite) {
            const fav = profileManager.getFavoriteElement();
            this.profileStatFavorite.textContent = fav ? fav.toUpperCase() : "—";
        }
        if (this.profileStatCountry) {
            this.profileStatCountry.textContent = p.country || "—";
        }
    }

    private initEventListeners(): void {
        if (this.profileBtn) {
            this.profileBtn.addEventListener("click", () => this.toggle());
        }

        if (this.profileClose) {
            this.profileClose.addEventListener("click", () => this.close());
        }

        if (this.profileModal) {
            this.profileModal.addEventListener("click", (e) => {
                if (e.target === this.profileModal && performance.now() - this.modalOpenedAt > 100) {
                    this.close();
                }
            });
        }

        if (this.profileChangeAlias) {
            this.profileChangeAlias.addEventListener("click", async () => {
                const { value: newAlias } = await Swal.fire({
                    title: 'Cambiar Alias',
                    input: 'text',
                    inputValue: this.currentAlias,
                    inputPlaceholder: 'Tu nuevo alias (máx 12)',
                    showCancelButton: true,
                    confirmButtonText: 'Guardar',
                    cancelButtonText: 'Cancelar',
                    inputValidator: (value) => {
                        if (!value || value.trim().length < 3) return 'El alias debe tener al menos 3 caracteres';
                        if (value.trim().length > 12) return 'El alias no puede superar los 12 caracteres';
                        return null;
                    }
                });

                if (newAlias) {
                    const trimmed = newAlias.trim();
                    this.currentAlias = trimmed;
                    safeSetStorage("alchem_alias", trimmed);
                    const aliasInput = document.getElementById("alias-input") as HTMLInputElement | null;
                    if (aliasInput) aliasInput.value = trimmed;
                    await profileManager.syncToDatabase({ alias: trimmed });
                    void this.refresh();
                }
            });
        }

        if (this.profileReset) {
            this.profileReset.addEventListener("click", async () => {
                const result = await Swal.fire({
                    title: '¿Reiniciar Progreso?',
                    text: 'Se perderán todas tus estadísticas locales. Esta acción no se puede deshacer.',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Sí, reiniciar',
                    cancelButtonText: 'Cancelar',
                    confirmButtonColor: '#ef4444',
                });

                if (result.isConfirmed) {
                    profileManager.resetProfile();
                    await profileManager.syncToDatabase({ alias: this.currentAlias });
                    void this.refresh();
                }
            });
        }
    }
}
